import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FilledTextFieldProps,
  FormControl,
  Grid,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
  TextField,
  TextFieldVariants,
  Typography,
} from '@mui/material'
import { Form, Formik } from 'formik'
import { inject } from 'mobx-react'
import moment from 'moment'
import { OptionsObject } from 'notistack'
import React, { useEffect, useState } from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { Priority } from 'src/models/enums/Priority.enum'
import { getInboxes, processAction } from 'src/services/WorkflowService'
import { IStores } from 'src/Stores'
import { InboxItemType } from 'src/utils/InboxItemType'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { AssignToEntryUserAndSendValidationSchema } from '../../validationSchemas/AssignToEntryUserAndSendValidationSchema'

const PRIORITY_OPTIONS: PriorityOption[] = Object.keys(Priority)
  .filter((key) => isNaN(Number(key)))
  .map((key) => ({
    id: Priority[key as keyof typeof Priority],
    label: key,
  }))

type UserOption = { id: string; description: string }
type PriorityOption = { id: number; label: string }

interface IAssignToEntryUserAndSendModalProps {
  isOpen: boolean
  closeModal: () => void
  action: InboxActionSummary
  rerouteToPath?: (path: string) => void
  inboxItemId: number
  inboxId: string
  associatedItemId: string
  data: ObjectiveProcessAction
  entryUser: string
  nextInboxId?: number
  nextInboxText?: string
  sendNotification?: (
    key: string,
    message: string,
    options?: OptionsObject,
    information?: string | undefined
  ) => void
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  userOptions?: UserOption[]
  getUserOptions?: (inboxId: string, actionId: string) => void
  assignDueDate?: string
  inboxType?: InboxItemType
  priority?: number
  isFixed?: boolean
}

const AssignToEntryUserAndSendModal: React.FC<IAssignToEntryUserAndSendModalProps> = (
  props
) => {
  const {
    isOpen,
    closeModal,
    action,
    entryUser,
    userOptions,
    getUserOptions,
    inboxType,
    inboxId,
    isFixed = true,
    sendNotification,
    handleProcessActionFinish,
    inboxItemId,
    data,
  } = props

  const [isLoading, setIsLoading] = useState(true)
  const [sendToOptions, setSendToOptions] = useState<UserOption[]>([])

  useEffect(() => {
    const initializeModal = async () => {
      setIsLoading(true)
      setSendToOptions([])

      try {
        const inboxes = await getInboxes(inboxType, isFixed)
        const filteredOptions: UserOption[] = inboxes
          .filter((inbox: any) => inbox.enabled)
          .map((inbox: any) => ({
            id: String(inbox.id),
            description: String(inbox.inboxName),
          }))
        setSendToOptions(filteredOptions)
      } catch {
        notifyAndClose('Failed to fetch inbox list.')
        return
      }

      if (getUserOptions) {
        getUserOptions(inboxId, action.id.toString())
      }

      setIsLoading(false)
    }

    if (isOpen) {
      initializeModal().then(() => {})
    }
  }, [isOpen, inboxType, inboxId, action.id, getUserOptions, isFixed])

  const notifyAndClose = (message: string) => {
    sendNotification?.('404', message, { variant: 'error' })
    closeModal()
  }

  const handleExecute = async (
    values: {
      comments: string
      assignUser: UserOption | null
      selectedSendTo: UserOption | null
      priority: PriorityOption | null
      dueDate: string
      requireComment: boolean
      pendDate: string
      pendNote: string
    },
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    const {
      comments,
      assignUser,
      selectedSendTo,
      priority,
      dueDate,
      pendDate,
      pendNote,
    } = values

    data.comment = comments
    data.assignUser = assignUser ? assignUser.id : undefined
    data.nextInboxIdOverride = selectedSendTo?.id ? Number(selectedSendTo.id) : undefined
    data.priorityOverride = priority?.id

    if (dueDate) {
      const [year, month, day] = dueDate.split('-').map(Number)

      data.assignDueDate = new Date(year, month - 1, day, 16, 0, 0, 0)
    } else {
      data.assignDueDate = undefined
    }

    if (pendDate) {
      data.pendDate = new Date(moment(pendDate, 'YYYY-MM-DD').toISOString())
      data.pendNote = pendNote
    } else {
      data.pendDate = undefined
      data.pendNote = undefined
    }

    setIsLoading(true)

    try {
      await processAction(data, action.id.toString(), inboxItemId.toString())
      handleProcessActionFinish?.(action)
    } finally {
      setSubmitting(false)
      setIsLoading(false)
      closeModal()
    }
  }

  return (
    <Dialog
      key={action.id}
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return closeModal()
      }}
      scroll="body"
      PaperProps={{ style: { overflow: 'visible' } }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar title={action.actionName.toUpperCase()} isModal />
      </DialogTitle>
      <DialogContent style={{ padding: '12px 12px 8px', overflow: 'visible' }}>
        {isLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1200,
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Formik
          initialValues={{
            comments: '',
            assignUser: null,
            selectedSendTo: null,
            priority: PRIORITY_OPTIONS.find((p) => p.label === 'Normal') || null,
            dueDate: '',
            requireComment: props.action.requireComment ?? false,
            pendDate: '',
            pendNote: '',
          }}
          validationSchema={AssignToEntryUserAndSendValidationSchema}
          onSubmit={handleExecute}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            isSubmitting,
            handleChange,
            handleBlur,
            setFieldTouched,
          }) => (
            <Form>
              <Grid container direction="column" spacing={2}>
                <Grid style={{ paddingBottom: '3px' }} item>
                  <Typography variant="h6">Assignment and Routing Details</Typography>
                </Grid>
                <Grid item container xs={12}>
                  <Grid item xs={6} container direction="row">
                    <Typography color="textSecondary">Sent From:&nbsp;&nbsp;</Typography>
                    <Typography>{entryUser}</Typography>
                  </Grid>
                </Grid>
                <Grid item>
                  <FormControl fullWidth variant="outlined">
                    <Autocomplete
                      size="small"
                      options={sendToOptions}
                      getOptionLabel={(option: { description: any }) =>
                        option?.description || ''
                      }
                      value={values.selectedSendTo}
                      onChange={(_: any, value: any) =>
                        setFieldValue('selectedSendTo', value)
                      }
                      onBlur={() => setFieldTouched('selectedSendTo', true)}
                      renderInput={(
                        params: React.JSX.IntrinsicAttributes & {
                          variant?: TextFieldVariants | undefined
                        } & Omit<
                            | OutlinedTextFieldProps
                            | StandardTextFieldProps
                            | FilledTextFieldProps,
                            'variant'
                          >
                      ) => (
                        <TextField
                          {...params}
                          label="Send To Inbox"
                          required
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          error={Boolean(touched.selectedSendTo && errors.selectedSendTo)}
                          helperText={touched.selectedSendTo && errors.selectedSendTo}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item>
                  <FormControl fullWidth variant="outlined">
                    <Autocomplete
                      size="small"
                      options={userOptions || []}
                      getOptionLabel={(option: { description: any }) =>
                        option?.description || ''
                      }
                      value={values.selectedSendTo ? values.assignUser : null}
                      onChange={(_: any, value: any) =>
                        setFieldValue('assignUser', value)
                      }
                      renderInput={(
                        params: React.JSX.IntrinsicAttributes & {
                          variant?: TextFieldVariants | undefined
                        } & Omit<
                            | OutlinedTextFieldProps
                            | StandardTextFieldProps
                            | FilledTextFieldProps,
                            'variant'
                          >
                      ) => (
                        <TextField
                          {...params}
                          label="Assigning To"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                      disabled={!values.selectedSendTo}
                    />
                  </FormControl>
                </Grid>
                <Grid item container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      inputProps={{ id: 'assignDueDate' }}
                      InputLabelProps={{ shrink: true }}
                      type="date"
                      name="dueDate"
                      variant="outlined"
                      value={values.dueDate}
                      label="Assign Due Date"
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.dueDate && touched.dueDate}
                      helperText={touched.dueDate && errors.dueDate}
                      InputProps={{ style: { height: '40px' } }}
                      disabled={!values.assignUser}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={PRIORITY_OPTIONS}
                      getOptionLabel={(option: { label: any }) => option.label}
                      value={values.priority}
                      onChange={(_: any, value: any) => setFieldValue('priority', value)}
                      renderInput={(
                        params: React.JSX.IntrinsicAttributes & {
                          variant?: TextFieldVariants | undefined
                        } & Omit<
                            | FilledTextFieldProps
                            | OutlinedTextFieldProps
                            | StandardTextFieldProps,
                            'variant'
                          >
                      ) => (
                        <TextField
                          {...params}
                          label="Priority"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid style={{ paddingBottom: '3px' }} item>
                  <Typography variant="h6">Pend</Typography>
                </Grid>
                <Grid item container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      inputProps={{ id: 'pendDate' }}
                      InputLabelProps={{ shrink: true }}
                      type="date"
                      name="pendDate"
                      variant="outlined"
                      value={values.pendDate}
                      label="Pend Date"
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.pendDate && touched.pendDate}
                      helperText={touched.pendDate && errors.pendDate}
                      InputProps={{ style: { height: '40px' } }}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    minRows={2}
                    multiline
                    label="Pend Note"
                    name="pendNote"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={values.pendNote}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.pendNote && touched.pendNote}
                    helperText={touched.pendNote && errors.pendNote}
                  />
                </Grid>
                {action.displayComment && (
                  <Grid item>
                    <TextField
                      label="Comments"
                      name="comments"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      required={action.requireComment}
                      multiline
                      minRows={4}
                      value={values.comments}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!errors.comments && touched.comments}
                      helperText={touched.comments && errors.comments}
                    />
                  </Grid>
                )}
                <Grid item>
                  <DialogActions>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={
                        isSubmitting ||
                        (action.requireComment && !values.comments) ||
                        !values.selectedSendTo
                      }
                    >
                      Execute
                    </Button>
                    <Button onClick={closeModal} data-cy="close">
                      Close
                    </Button>
                  </DialogActions>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export { AssignToEntryUserAndSendModal }

const InjectedAssignToEntryUserAndSendModal = inject<
  IStores,
  IAssignToEntryUserAndSendModalProps,
  Partial<IAssignToEntryUserAndSendModalProps>,
  any
>((stores: IStores) => ({
  rerouteToPath: stores.global.rerouteToPath,
  sendNotification: stores.notifications.sendNotification,
  getUserOptions: stores.users.getUserOptions,
  userOptions: stores.users.userOptions,
}))(AssignToEntryUserAndSendModal)

export default InjectedAssignToEntryUserAndSendModal
